import React, {useState} from "react";
import {Alert, Box, Button, Link, MenuItem, Stack, TextField, Typography} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers-pro";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {callMithraMcp} from "~/pages/chat-agent/mithra_mcp/callMithraMcp";
import type {InitiativeLeverOption, InitiativeProposal} from "./initiativeProposal";
import {
    readCommitted,
    readDiscarded,
    writeCommitted,
    writeDiscarded,
} from "./initiativeDraftStorage";
import type {CommitResult, InitiativeFields} from "./initiativeDraftStorage";

type Props = {
    proposal: InitiativeProposal;
    messageId: string;
    /** A newer draft exists in this chat: this one can no longer be committed. */
    superseded?: boolean;
};

function draftKey(proposal: InitiativeProposal): string {
    return [
        proposal.title,
        proposal.lever,
        proposal.description,
        proposal.priority,
        proposal.start_date,
    ].join("\u0000");
}

function leverOptions(proposal: InitiativeProposal, selected: string): InitiativeLeverOption[] {
    if (proposal.levers.some(lever => lever.name === selected)) return proposal.levers;
    return [{group: "", title: selected, description: "", name: selected}, ...proposal.levers];
}

function priorityOptions(proposal: InitiativeProposal, selected: string): string[] {
    if (proposal.priorities.includes(selected)) return proposal.priorities;
    return [selected, ...proposal.priorities];
}

async function commitInitiative(fields: InitiativeFields): Promise<CommitResult> {
    const data = await callMithraMcp("sit", "create_initiative", fields);
    if (typeof data.id !== "number" || typeof data.url !== "string") {
        const message = typeof data.error === "string" ? data.error : "Could not create the initiative.";
        throw new Error(message);
    }
    return {id: data.id, url: data.url, duplicate: data.duplicate === true};
}

export const InitiativeDraftCard: React.FC<Props> = ({proposal, messageId, superseded = false}) => {
    const key = draftKey(proposal);
    const [restored] = useState(() => readCommitted(messageId, key));
    const initial = restored?.fields ?? proposal;
    const [seed, setSeed] = useState(key);
    const [title, setTitle] = useState(initial.title);
    const [lever, setLever] = useState(initial.lever);
    const [description, setDescription] = useState(initial.description);
    const [priority, setPriority] = useState(initial.priority);
    const [startDate, setStartDate] = useState(initial.start_date);
    const [committing, setCommitting] = useState(false);
    const [error, setError] = useState("");
    const [committed, setCommitted] = useState<CommitResult | null>(restored?.result ?? null);
    const [discarded, setDiscarded] = useState(() => readDiscarded(messageId));

    if (seed !== key) {
        setSeed(key);
        setTitle(proposal.title);
        setLever(proposal.lever);
        setDescription(proposal.description);
        setPriority(proposal.priority);
        setStartDate(proposal.start_date);
        setError("");
        setCommitted(null);
    }

    const locked = committing || committed != null;
    const start = startDate ? dayjs(startDate) : null;
    const canCommit = Boolean(
        title.trim() && description.trim() && lever && priority && start?.isValid(),
    );

    const handleCommit = async () => {
        if (!canCommit || locked || !start?.isValid()) return;
        setCommitting(true);
        setError("");
        try {
            const fields: InitiativeFields = {
                title: title.trim(),
                description: description.trim(),
                lever,
                priority,
                start_date: start.format("YYYY-MM-DD"),
            };
            const result = await commitInitiative(fields);
            writeCommitted(messageId, {draftKey: key, fields, result});
            setCommitted(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not create the initiative.");
        } finally {
            setCommitting(false);
        }
    };

    const handleDiscard = () => {
        writeDiscarded(messageId, true);
        setDiscarded(true);
    };

    const handleUndo = () => {
        writeDiscarded(messageId, false);
        setDiscarded(false);
    };

    if (discarded) {
        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{width: "100%", mt: 2}}
            >
                <Typography variant="body2" color="text.secondary">
                    Initiative draft discarded.
                </Typography>
                <Button size="small" onClick={handleUndo}>Undo</Button>
            </Stack>
        );
    }

    if (superseded && !committed) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{width: "100%", mt: 2}}>
                Replaced by a newer initiative draft.
            </Typography>
        );
    }

    return (
        <Stack
            spacing={2.5}
            sx={{
                width: "100%",
                mt: 2,
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"},
                    gap: 2,
                }}
            >
                <TextField
                    label="Title"
                    value={title}
                    onChange={event => setTitle(event.target.value)}
                    fullWidth
                    disabled={locked}
                />
                <TextField
                    select
                    label="Priority"
                    value={priority}
                    onChange={event => setPriority(event.target.value)}
                    fullWidth
                    disabled={locked}
                >
                    {priorityOptions(proposal, priority).map(option => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Lever"
                    value={lever}
                    onChange={event => setLever(event.target.value)}
                    fullWidth
                    disabled={locked}
                >
                    {leverOptions(proposal, lever).map(option => (
                        <MenuItem key={option.name} value={option.name}>{option.name}</MenuItem>
                    ))}
                </TextField>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Start date"
                        value={start?.isValid() ? start : null}
                        onChange={value => setStartDate(value?.isValid() ? value.format("YYYY-MM-DD") : "")}
                        disabled={locked}
                        slotProps={{textField: {fullWidth: true}}}
                    />
                </LocalizationProvider>
            </Box>
            <TextField
                label="Description"
                value={description}
                onChange={event => setDescription(event.target.value)}
                fullWidth
                multiline
                minRows={4}
                disabled={locked}
            />
            {error && <Alert severity="error">{error}</Alert>}
            {committed && (
                <Alert severity={committed.duplicate ? "info" : "success"}>
                    {committed.duplicate ? "That initiative already exists. " : "Initiative created. "}
                    <Link href={committed.url} target="_blank" rel="noopener noreferrer">
                        Open initiative
                    </Link>
                </Alert>
            )}
            <Stack direction="row" spacing={1}>
                <Button
                    variant="contained"
                    onClick={handleCommit}
                    disabled={!canCommit || locked}
                    loading={committing}
                >
                    Commit
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleDiscard}
                    disabled={committing || committed != null}
                >
                    Discard
                </Button>
            </Stack>
        </Stack>
    );
};
